import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { resources, Resource, ResourceType } from '../../mocks/resources';

const TYPE_LABELS: Record<ResourceType, string> = {
  guide: 'Guide',
  webinar: 'Webinar',
  template: 'Template',
  report: 'Market Report',
  video: 'Video',
};

const TYPE_COLORS: Record<ResourceType, string> = {
  guide: 'bg-amber-100 text-amber-700',
  webinar: 'bg-navy/10 text-navy',
  template: 'bg-green-100 text-green-700',
  report: 'bg-orange-100 text-orange-700',
  video: 'bg-red-100 text-red-700',
};

const TYPE_ICONS: Record<ResourceType, string> = {
  guide: 'ri-book-open-line',
  webinar: 'ri-video-chat-line',
  template: 'ri-file-copy-2-line',
  report: 'ri-bar-chart-box-line',
  video: 'ri-play-circle-line',
};

const CATEGORY_FILTERS = ['All', 'Beginner', 'Intermediate', 'Advanced', 'Legal', 'Finance', 'Market'];
const TYPE_FILTERS: Array<ResourceType | 'all'> = ['all', 'guide', 'webinar', 'template', 'report', 'video'];

export default function Resources() {
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [typeFilter, setTypeFilter] = useState<ResourceType | 'all'>('all');

  const filtered = resources.filter((r) => {
    const matchCat = categoryFilter === 'All' || r.category === categoryFilter;
    const matchType = typeFilter === 'all' || r.type === typeFilter;
    return matchCat && matchType;
  });

  const featured = resources.filter((r) => r.featured);

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative bg-navy pt-40 pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://public.readdy.ai/ai/img_res/7769625d6eb590bbd31d7ce62f0ad39c.jpg"
              alt="Resources"
              className="w-full h-full object-cover object-top opacity-15"
            />
          </div>
          <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <span className="inline-block px-4 py-1.5 border border-gold/50 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Knowledge Center
            </span>
            <h1 className="font-playfair text-5xl md:text-6xl font-bold text-white mb-6">
              Master Foreclosure Investment
            </h1>
            <p className="text-white/70 text-xl max-w-2xl mx-auto">
              Exclusive guides, webinars, legal templates, and market reports to help you succeed with tax-delinquent properties.
            </p>
          </div>
        </section>

        {/* Stats Cards */}
        <section className="bg-cream py-12">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { count: '47', label: 'Investment Guides', icon: 'ri-book-open-line', color: 'bg-amber-500' },
                { count: '24', label: 'Monthly Webinars', icon: 'ri-video-chat-line', color: 'bg-teal' },
                { count: '31', label: 'Legal Templates', icon: 'ri-file-copy-2-line', color: 'bg-navy' },
                { count: '18', label: 'Market Reports', icon: 'ri-bar-chart-box-line', color: 'bg-orange-500' },
              ].map((item) => (
                <div key={item.label} className={`${item.color} text-white rounded-xl p-6 flex items-center gap-4`}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 flex-shrink-0">
                    <i className={`${item.icon} text-white text-xl`} />
                  </div>
                  <div>
                    <p className="font-playfair text-3xl font-bold">{item.count}</p>
                    <p className="text-white/80 text-xs">{item.label}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Resources */}
        <section className="bg-white py-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <h2 className="font-playfair text-3xl font-bold text-navy mb-8">Featured Resources</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Big Feature Card */}
              <div className="lg:col-span-2">
                {featured[0] && (
                  <Link to="/membership-benefits" className="group block rounded-2xl overflow-hidden border border-gray-100 hover:border-gold/30 transition-all">
                    <div className="h-64 overflow-hidden">
                      <img
                        src={featured[0].imageUrl}
                        alt={featured[0].title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-8">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-md ${TYPE_COLORS[featured[0].type]}`}>
                          <i className={`${TYPE_ICONS[featured[0].type]} text-xs`} />
                          {TYPE_LABELS[featured[0].type]}
                        </span>
                        <span className="text-xs text-gray-400">{featured[0].readTime || featured[0].duration}</span>
                        <span className="ml-auto text-xs text-gray-300">{featured[0].date}</span>
                      </div>
                      <h3 className="font-playfair text-2xl font-bold text-navy mb-2">{featured[0].title}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{featured[0].description}</p>
                    </div>
                  </Link>
                )}
              </div>

              {/* Side Featured Cards */}
              <div className="flex flex-col gap-5">
                {featured.slice(1, 3).map((resource) => (
                  <Link key={resource.id} to="/membership-benefits" className="group flex gap-4 bg-cream rounded-xl p-4 border border-gray-100 hover:border-gold/30 transition-all">
                    <div className="w-24 h-20 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src={resource.imageUrl}
                        alt={resource.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md mb-1.5 ${TYPE_COLORS[resource.type]}`}>
                        <i className={`${TYPE_ICONS[resource.type]} text-xs`} />
                        {TYPE_LABELS[resource.type]}
                      </span>
                      <h4 className="font-semibold text-navy text-sm leading-snug line-clamp-2">{resource.title}</h4>
                      <p className="text-xs text-gray-400 mt-1">{resource.readTime || resource.duration}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* All Resources */}
        <section className="bg-cream py-16">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex flex-col md:flex-row md:items-center gap-6 mb-10">
              <h2 className="font-playfair text-3xl font-bold text-navy">All Resources</h2>
              <div className="md:ml-auto flex flex-wrap gap-3">
                {/* Type Filters */}
                <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1 flex-wrap">
                  {TYPE_FILTERS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTypeFilter(t)}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                        typeFilter === t ? 'bg-navy text-white' : 'text-gray-500 hover:text-navy'
                      }`}
                    >
                      {t === 'all' ? 'All Types' : TYPE_LABELS[t]}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Category Filters */}
            <div className="flex gap-2 mb-8 flex-wrap">
              {CATEGORY_FILTERS.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all cursor-pointer whitespace-nowrap ${
                    categoryFilter === cat
                      ? 'border-navy bg-navy text-white'
                      : 'border-gray-200 text-gray-500 hover:border-navy/40 hover:text-navy bg-white'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((resource: Resource) => (
                <Link
                  key={resource.id}
                  to="/membership-benefits"
                  className="group bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-gold/30 transition-all duration-300"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={resource.imageUrl}
                      alt={resource.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-md ${TYPE_COLORS[resource.type]}`}>
                        <i className={`${TYPE_ICONS[resource.type]} text-xs`} />
                        {TYPE_LABELS[resource.type]}
                      </span>
                      <span className="text-xs text-gray-300 ml-auto">{resource.category}</span>
                    </div>
                    <h3 className="font-semibold text-navy text-sm leading-snug mb-2 line-clamp-2">{resource.title}</h3>
                    <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed mb-3">{resource.description}</p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-50">
                      <span className="text-xs text-gray-400">{resource.readTime || resource.duration}</span>
                      <span className="text-xs text-gold font-semibold flex items-center gap-1">
                        Read More <i className="ri-arrow-right-line text-xs" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <i className="ri-search-line text-gray-300 text-5xl mb-3 block" />
                <p className="text-gray-500">No resources match your current filters.</p>
                <button onClick={() => { setCategoryFilter('All'); setTypeFilter('all'); }} className="mt-3 text-gold text-sm font-semibold cursor-pointer hover:underline">
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-navy py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
            <h2 className="font-playfair text-4xl font-bold text-white mb-4">
              Unlock the Full Resource Library
            </h2>
            <p className="text-white/60 mb-8 max-w-lg mx-auto">Members get full access to all 120+ resources including exclusive webinars, legal templates, and monthly market reports.</p>
            <Link to="/membership-benefits" className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-navy-dark font-bold rounded-lg hover:bg-gold-light transition-colors whitespace-nowrap">
              Start Free Trial <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
